import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { computed, readonly, ref } from 'vue';

import { HTTP_STATUS } from '../constants.js';
import { createMutationQueue, type MutationQueue } from '../storage/mutationQueue.js';
import { createOfflineStorage, type OfflineStorage } from '../storage/offlineStorage.js';
import type { ConflictData, MutationMethod, MutationQueueItem, OfflineMutationOptions } from '../types.js';

import { useNetworkStatus } from './useNetworkStatus.js';

// Cache of queue instances per portal
const queueInstances = new Map<string, MutationQueue>();
const storageInstances = new Map<string, OfflineStorage>();

/**
 * Gets or creates a mutation queue instance for a portal
 */
function getQueue(portalName: string): MutationQueue {
  if (!queueInstances.has(portalName)) {
    queueInstances.set(portalName, createMutationQueue({ portalName }));
  }
  return queueInstances.get(portalName)!;
}

/**
 * Gets or creates an offline storage instance for a portal
 */
function getStorage(portalName: string): OfflineStorage {
  if (!storageInstances.has(portalName)) {
    storageInstances.set(portalName, createOfflineStorage({ portalName }));
  }
  return storageInstances.get(portalName)!;
}

/**
 * Options for useOfflineMutation
 */
export interface UseOfflineMutationOptions<TData = unknown, TError = Error, TVariables = unknown> extends OfflineMutationOptions<
  TData,
  TError,
  TVariables
> {
  /** Portal name for namespace isolation */
  portalName: string;
  /** Query keys to invalidate on success */
  invalidateQueries?: unknown[][];
  /** Callback when mutation is queued (offline) */
  onQueued?: (item: MutationQueueItem<TVariables>) => void;
  /** Callback when conflict is detected */
  onConflictDetected?: (conflict: ConflictData) => void;
}

/**
 * Composable for offline-aware mutations with automatic queueing
 *
 * Extends TanStack Query's useMutation with:
 * - Automatic mutation queueing when offline
 * - Optimistic updates with rollback support
 * - Conflict detection (409 responses)
 * - Queue status tracking
 *
 * @example
 * ```typescript
 * const { mutate, isQueued, queuePosition } = useOfflineMutation(
 *   (invoice) => api.updateInvoice(invoice),
 *   {
 *     portalName: 'client-portal',
 *     resourceKey: (vars) => `invoice:${vars.id}`,
 *     onConflict: 'prompt',
 *     optimisticUpdate: (vars, current) => ({ ...current, ...vars }),
 *   }
 * )
 * ```
 */
export function useOfflineMutation<TData = unknown, TError = Error, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: UseOfflineMutationOptions<TData, TError, TVariables>,
) {
  const {
    portalName,
    resourceKey,
    method = 'PUT',
    onConflict = 'prompt',
    optimisticUpdate,
    queueOffline = true,
    // `retry` is offline-specific config (maxAttempts/baseDelay) and must not be forwarded to
    // TanStack's useMutation via `...mutationOptions`, which expects a different `retry` shape.
    // The leading underscore marks it intentionally omitted from the rest.
    retry: _retry,
    invalidateQueries = [],
    onQueued,
    onConflictDetected,
    ...mutationOptions
  } = options;

  const queue = getQueue(portalName);
  const storage = getStorage(portalName);
  const { isOnline } = useNetworkStatus();
  const queryClient = useQueryClient();

  // Internal state
  const queuedMutationId = ref<string | null>(null);
  const pendingConflict = ref<ConflictData | null>(null);

  /**
   * Resolves the resource key for a mutation
   */
  function resolveResourceKey(variables: TVariables): string {
    return typeof resourceKey === 'function' ? resourceKey(variables) : resourceKey;
  }

  /**
   * Applies optimistic update to cache
   */
  async function applyOptimisticUpdate(variables: TVariables, key: string): Promise<{ optimisticData: unknown; rollbackData: unknown } | null> {
    if (!optimisticUpdate) return null;

    const cached = await storage.get(key);
    const currentData = cached?.data ?? null;

    if (currentData === null) return null;

    const optimisticData = optimisticUpdate(variables, currentData);

    // Save optimistic update
    await storage.set(key, optimisticData, {
      version: (cached?.metadata.version ?? 0) + 1,
    });

    return {
      optimisticData,
      rollbackData: currentData,
    };
  }

  /**
   * Rolls back an optimistic update
   */
  async function rollbackOptimisticUpdate(key: string, rollbackData: unknown): Promise<void> {
    if (rollbackData !== undefined) {
      await storage.set(key, rollbackData);
    }
  }

  /**
   * Queues a mutation for later processing
   */
  async function queueMutation(variables: TVariables): Promise<MutationQueueItem<TVariables>> {
    const key = resolveResourceKey(variables);

    // Apply optimistic update
    const optimistic = await applyOptimisticUpdate(variables, key);

    // Get current version for conflict detection
    const metadata = await storage.getMetadata(key);
    const version = metadata?.version;

    // Enqueue the mutation
    const item = await queue.enqueue(key, method as MutationMethod, variables, {
      optimisticData: optimistic?.optimisticData,
      rollbackData: optimistic?.rollbackData,
      resourceVersion: version,
    });

    queuedMutationId.value = item.id;
    onQueued?.(item);

    return item;
  }

  /**
   * Handles conflict response from server
   */
  function handleConflict(variables: TVariables, error: unknown, mutationId?: string): ConflictData | null {
    // Check if error is a conflict response
    if (!isConflictError(error)) return null;

    const conflictResponse = extractConflictData(error);
    if (!conflictResponse) return null;

    const key = resolveResourceKey(variables);
    const conflict: ConflictData = {
      id: `conflict-${Date.now()}`,
      resourceKey: key,
      mutationId: mutationId ?? '',
      localData: conflictResponse.localData,
      serverData: conflictResponse.serverData,
      localVersion: conflictResponse.localVersion,
      serverVersion: conflictResponse.serverVersion,
      detectedAt: Date.now(),
      serverTimestamp: conflictResponse.serverTimestamp,
      conflictingFields: detectConflictingFields(conflictResponse.localData, conflictResponse.serverData),
    };

    pendingConflict.value = conflict;
    onConflictDetected?.(conflict);

    // Mark mutation as conflict if queued
    if (mutationId) {
      void queue.markConflict(mutationId);
    }

    return conflict;
  }

  /**
   * Wrapped mutation function with offline support
   */
  async function wrappedMutationFn(variables: TVariables): Promise<TData> {
    const key = resolveResourceKey(variables);

    // If offline and queueing is enabled, queue the mutation
    if (!isOnline.value && queueOffline) {
      await queueMutation(variables);
      // Return a placeholder or throw to indicate queued
      throw new OfflineMutationQueuedError('Mutation queued for offline sync');
    }

    // Online: execute mutation
    try {
      const result = await mutationFn(variables);

      // Update cache with server response
      if (result && typeof result === 'object') {
        const version = 'version' in result ? (result as Record<string, unknown>).version : undefined;
        await storage.set(key, result, { version: version as number | undefined });
      }

      // Clear queued mutation ID if this was a retry
      queuedMutationId.value = null;

      return result;
    } catch (error) {
      // Check for conflict
      const conflict = handleConflict(variables, error);

      if (conflict && onConflict === 'server-wins') {
        // Auto-resolve with server data
        await storage.set(key, conflict.serverData, {
          version: conflict.serverVersion,
        });
        // Re-throw to trigger onError
      } else if (conflict && onConflict === 'client-wins') {
        // Retry with force flag (if supported by API)
        // For now, just re-throw
      }

      throw error;
    }
  }

  // Use TanStack Mutation
  const mutation = useMutation<TData, TError, TVariables>({
    mutationFn: wrappedMutationFn,
    onSuccess: async (data, variables) => {
      // Invalidate related queries
      for (const queryKey of invalidateQueries) {
        await queryClient.invalidateQueries({ queryKey });
      }
      mutationOptions.onSuccess?.(data, variables, undefined as never);
    },
    onError: async (error, variables) => {
      // Rollback optimistic update if not a queued mutation
      if (!(error instanceof OfflineMutationQueuedError)) {
        const key = resolveResourceKey(variables);
        const queuedItem = queuedMutationId.value ? await queue.getById(queuedMutationId.value) : null;
        if (queuedItem?.rollbackData) {
          await rollbackOptimisticUpdate(key, queuedItem.rollbackData);
        }
      }
      mutationOptions.onError?.(error, variables, undefined as never);
    },
    ...mutationOptions,
  });

  // Computed properties
  const isQueued = computed(() => queuedMutationId.value !== null);

  const queuePosition = computed(async () => {
    if (!queuedMutationId.value) return null;
    const all = await queue.getAll();
    const index = all.findIndex((item) => item.id === queuedMutationId.value);
    return index >= 0 ? index + 1 : null;
  });

  const hasConflict = computed(() => pendingConflict.value !== null);

  /**
   * Cancels a queued mutation
   */
  async function cancelQueuedMutation(): Promise<void> {
    if (!queuedMutationId.value) return;

    const item = await queue.getById(queuedMutationId.value);
    if (item?.rollbackData) {
      await rollbackOptimisticUpdate(item.resourceKey, item.rollbackData);
    }

    await queue.remove(queuedMutationId.value);
    queuedMutationId.value = null;
  }

  /**
   * Resolves a conflict with the specified strategy
   */
  async function resolveConflict(resolution: 'local' | 'server' | 'merge', mergedData?: unknown): Promise<void> {
    if (!pendingConflict.value) return;

    const conflict = pendingConflict.value;
    let resolvedData: unknown;

    switch (resolution) {
      case 'local':
        resolvedData = conflict.localData;
        break;
      case 'server':
        resolvedData = conflict.serverData;
        break;
      case 'merge':
        resolvedData = mergedData ?? conflict.serverData;
        break;
    }

    // Update local cache
    await storage.set(conflict.resourceKey, resolvedData, {
      version: conflict.serverVersion + 1,
    });

    // Remove from conflict state
    pendingConflict.value = null;

    // Remove queued mutation if it exists
    if (conflict.mutationId) {
      await queue.remove(conflict.mutationId);
    }
  }

  return {
    // TanStack Mutation result
    ...mutation,

    // Offline-specific state
    isQueued,
    queuePosition,
    queuedMutationId: readonly(queuedMutationId),
    hasConflict,
    pendingConflict: readonly(pendingConflict),

    // Offline-specific methods
    cancelQueuedMutation,
    resolveConflict,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Custom error for queued mutations
 */
export class OfflineMutationQueuedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OfflineMutationQueuedError';
  }
}

/**
 * Checks if an error is a conflict (409) response
 */
function isConflictError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  // Check for axios-style error
  if ('response' in error) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status === HTTP_STATUS.CONFLICT;
  }

  // Check for fetch-style error
  if ('status' in error) {
    return (error as { status: number }).status === HTTP_STATUS.CONFLICT;
  }

  return false;
}

/**
 * Extracts conflict data from error response
 */
function extractConflictData(error: unknown): {
  localVersion: number;
  serverVersion: number;
  localData: Record<string, unknown>;
  serverData: Record<string, unknown>;
  serverTimestamp: string;
} | null {
  if (!error || typeof error !== 'object') return null;

  let data: unknown;

  // Extract data from axios-style error
  if ('response' in error) {
    data = (error as { response?: { data?: unknown } }).response?.data;
  } else if ('data' in error) {
    data = (error as { data: unknown }).data;
  }

  if (!data || typeof data !== 'object') return null;

  const conflictData = data as Record<string, unknown>;

  if ('localVersion' in conflictData && 'serverVersion' in conflictData && 'localData' in conflictData && 'serverData' in conflictData) {
    return {
      localVersion: conflictData.localVersion as number,
      serverVersion: conflictData.serverVersion as number,
      localData: conflictData.localData as Record<string, unknown>,
      serverData: conflictData.serverData as Record<string, unknown>,
      serverTimestamp: (conflictData.serverTimestamp as string) ?? new Date().toISOString(),
    };
  }

  return null;
}

/**
 * Detects which fields differ between two objects
 */
function detectConflictingFields(local: Record<string, unknown>, server: Record<string, unknown>): string[] {
  const fields: string[] = [];
  const allKeys = new Set([...Object.keys(local), ...Object.keys(server)]);

  for (const key of allKeys) {
    if (JSON.stringify(local[key]) !== JSON.stringify(server[key])) {
      fields.push(key);
    }
  }

  return fields;
}
